/* eslint-disable no-unused-vars */
/* eslint-disable camelcase */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import NavTabs from '@app/components/molecules/NavTabs';
import { useRouter } from 'next/router';
import { useUser } from '@app/contexts/userContext';
import { useChat, useGroupChatApi, useChatConnectionApi } from '@app/contexts/chatContext';
import { EVENT_TYPES } from '@app/config/utils';
import { OPPORTUNITY_TRACK_ROLE } from '@app/constants/roles';
import {
  Grid,
  Typography,
  Paper,
  Box,
  Drawer,
  SwipeableDrawer,
  Autocomplete,
  TextField,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useTranslation } from '@app/config/i18n';
import Link from '@atoms/Link';
import HeaderWithSidebar from '@app/components/templates/HeaderWithSidebar';
import GeneralFields from '@app/components/organisms/opportunity-wizard/WizardForm/GeneralFields';
import EventDiscussionPanel from '@app/components/organisms/opportunities/EventDiscusionPanel';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import RoundedButton from '@app/components/atoms/RoundedButton';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { colors } from '@app/config/colors';
import Button from '@app/components/atoms/Button';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_CHAT_GROUP } from '@app/apollo/mutations';
import { create } from 'lodash';
import { Description } from '@mui/icons-material';
import { OPPORTUNITY_SUBMISSION_APPLICATIONS } from '@app/apollo/queries';
import Spacer from '@app/components/atoms/Spacer';
import { NavTabsContainer, StyledAccordion, StyledSummaryBoxes, BodyContainer } from './style';
import OpportunityMainTemplate from '../OpportunityMainTemplate';

// Define roles for chat group members

const roleOptions = [
  { label: 'Admin', value: 'admin' },
  { label: 'Speaker', value: 'speaker' },
  { label: 'Member', value: 'member' },
];

const ViewOpportunityDashboardTemplate = ({
  children,
  opportunity,
  opportunityMemberRole,
  viewApplication,
  refetch,
}) => {
  const { user, isOrganiserOrInvitedAdmin, isOpportunityChatMember } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const router = useRouter();
  const slug = router.query ? router.query.slug : null;
  const { t } = useTranslation('common');
  const [tab, setTab] = useState(t('common:tabs'));

  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [participants, setParticipants] = useState([]);

  const { chatState } = useChat();
  const { joinGroupChat } = useGroupChatApi();
  const { connectionStatus } = useChatConnectionApi();

  const isAdminOrMemberOfEvent = isOrganiserOrInvitedAdmin(opportunity) || isOpportunityChatMember(opportunity);

  // Simple room joining when dashboard loads
  useEffect(() => {
    console.log('🏠 Dashboard: useEffect triggered with:', {
      opportunityId: opportunity?.id,
      chatGroupId: opportunity?.chatGroup?.id,
      userId: user?.id,
      hasJoinGroupChat: !!joinGroupChat,
    });

    if (opportunity?.id && opportunity?.chatGroup && user) {
      console.log('🏠 Dashboard: Joining chat room for opportunity:', opportunity.id);

      // Simply join the group chat room - stay joined until dashboard unmounts
      joinGroupChat(opportunity.chatGroup);
    } else {
      console.log('🏠 Dashboard: Skipping join - missing required data');
    }

    // Cleanup: leave room when dashboard unmounts
    return () => {
      if (opportunity?.id && opportunity?.chatGroup) {
        console.log('🏠 Dashboard: Leaving chat room for opportunity:', opportunity.id);
        // Note: We might want to keep the connection alive depending on app behavior
        // For now, we'll leave it to the chat service to manage
      }
    };
  }, [opportunity?.id, opportunity?.chatGroup?.id, user?.id]);

  const isConnected = connectionStatus === 'connected' || true;

  const toggleChatDrawer = () => {
    setChatDrawerOpen((prev) => !prev);
  };

  useEffect(() => {
    if (router.pathname?.endsWith('view')) {
      setTab(t('common:about'));
    } else if (router.pathname?.endsWith('program-agenda')) {
      setTab(t('common:program agenda'));
    } else if (router.pathname?.endsWith('practical-info')) {
      setTab(t('common:useful info'));
    } else {
      const lastWord = router.pathname?.split('/').pop();
      setTab(lastWord);
    }
  }, [router.pathname]);

  useEffect(() => {
    if (router.query?.openChat === 'true') {
      setTimeout(() => {
        setChatDrawerOpen(true);
      }, 300);

      // Remove the query parameter from URL (clean URL)
      const { openChat, ...restQuery } = router.query;
      router.replace(
        {
          pathname: router.pathname,
          query: restQuery,
        },
        undefined,
        { shallow: true }
      );
    }
  }, [router.query?.openChat]);

  const opportunityHaveReviews =
    opportunity?.event_type.slug === EVENT_TYPES.CONFERENCE_MULTI_TRACK ||
    opportunity?.event_type.slug === EVENT_TYPES.CONFERENCE_SINGLE_TRACK ||
    opportunity?.event_type.slug === EVENT_TYPES.PHD_SCHOOL_WITH_REVIEW_PROCESS ||
    opportunity?.event_type.slug === EVENT_TYPES.WORKSHOP ||
    opportunity?.event_type.slug === EVENT_TYPES.SYMPOSIUM ||
    opportunity?.event_type.slug === EVENT_TYPES.COMPETITIONS ||
    opportunity?.event_type.slug === EVENT_TYPES.SCIENCE_EXHIBITIONS_AND_SCIENCE_FESTIVAL;

  const opportunityHaveSubmissions =
    opportunity?.event_type.slug === EVENT_TYPES.CONFERENCE_MULTI_TRACK ||
    opportunity?.event_type.slug === EVENT_TYPES.CONFERENCE_SINGLE_TRACK ||
    opportunity?.event_type.slug === EVENT_TYPES.PHD_SCHOOL_WITH_REVIEW_PROCESS ||
    opportunity?.event_type.slug === EVENT_TYPES.WORKSHOP ||
    opportunity?.event_type.slug === EVENT_TYPES.SYMPOSIUM;

  const opportunityHaveCommittees =
    opportunity?.event_type.slug === EVENT_TYPES.CONFERENCE_MULTI_TRACK ||
    opportunity?.event_type.slug === EVENT_TYPES.CONFERENCE_SINGLE_TRACK ||
    opportunity?.event_type.slug === EVENT_TYPES.PHD_SCHOOL_WITH_REVIEW_PROCESS ||
    opportunity?.event_type.slug === EVENT_TYPES.PHD_SCHOOL_WITHOUT_REVIEW_PROCESS ||
    opportunity?.event_type.slug === EVENT_TYPES.SYMPOSIUM ||
    opportunity?.event_type.slug === EVENT_TYPES.WORKSHOP;

  const opportunityMenu = [
    {
      route: 'view_opportunity',
      title: 'About',
      params: { slug },
    },
    {
      route: 'opportunity_sponsor',
      title: 'Sponsors',
      params: { slug },
    },
    {
      route: 'opportunity_committee',
      title: 'Committees',
      params: { slug },
    },
    {
      route: 'opportunity_submissions',
      title: 'Submissions',
      // opportunityMemberRole === OPPORTUNITY_TRACK_ROLE.ORGANIZER ? 'Submissions' : 'Submission',
      params: { slug },
    },
    {
      route: 'opportunity_reviews',
      title: 'Reviews',
      params: { slug },
    },
    {
      route: 'opportunity_registrations',
      title: 'Registrations',
      // opportunityMemberRole === OPPORTUNITY_TRACK_ROLE.ORGANIZER
      //   ? 'Registrations'
      //   : 'Registration',
      params: { slug },
    },
    {
      route: 'opportunity_feedbacks',
      title: 'Feedbacks',
      params: { slug },
    },
    {
      route: 'opportunity_program_agenda',
      title: 'Program Agenda',
      params: { slug },
    },
    {
      route: 'opportunity_practical_info',
      title: 'Useful Info',
      params: { slug },
    },
  ];

  const {
    loading,
    error,
    data: { opportunitySubmissionApplications } = {},
    refetch: refetchSubmissions,
  } = useQuery(OPPORTUNITY_SUBMISSION_APPLICATIONS, {
    variables: {
      slug: opportunity.slug,
      filters: {
        q: '',
        sort: { field: 'RECENT', asc: false },
        status: [],
        registration_status: [],
        payment_status: [],
        author: [],
        title: [],
      },
      first: 100,
      page: 1,
    },
  });

  const isExternal = opportunity?.is_external;

  const [createGroupChatMutation, { loading: creatingGroup }] = useMutation(CREATE_CHAT_GROUP);

  const handleSelectUsers = (event, selectedUsers) => {
    // keep roles if already assigned
    const updated = selectedUsers.map((usr) => {
      const existing = participants.find((p) => p.userId === usr.id);
      return existing || { userId: usr.id, name: usr.name, role: 'member' };
    });
    setParticipants(updated);
  };

  const handleRoleChange = (userId, role) => {
    setParticipants((prev) => prev.map((p) => (p.userId === userId ? { ...p, role } : p)));
  };

  const handleCreateChatGroup = () => {
    const members = participants.map((p) => ({
      userId: p.userId,
      role: p.role,
    }));

    createGroupChatMutation({
      variables: {
        input: {
          name: `${opportunity.slug}.chat-group`,
          description: `Chat group for ${opportunity.slug}`,
          opportunityId: opportunity.id,
          members,
        },
      },
      // onCompleted: (data) => {
      //   if (data?.createChatGroup) {
      //     refetch();
      //   }
      // },
      onCompleted: async (data) => {
        if (data?.createChatGroup) {
          // Immediately join the newly created group so UI updates from chat store
          try {
            await joinGroupChat(data.createChatGroup);
          } catch (e) {
            // ignore join errors - refetch will still update server state
          }

          // Ensure parent data is refreshed and drawer is open
          await refetch();
          setChatDrawerOpen(true);
        }
      },
    });
  };

  const OppSubs = opportunitySubmissionApplications?.data ?? [];

  const activeGroupFromStore = chatState?.activeGroupChat;

  const chatDrawer = user ? (
    <Drawer
      anchor="right"
      open={chatDrawerOpen}
      onClose={toggleChatDrawer}
      // PaperProps={{
      //   sx: {
      //     width: { xs: '100%', sm: '33%' },
      //   },
      // }}
      hideBackdrop={isMobile} // <-- removes the grey overlay
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '33%' },
          top: { xs: '64px', sm: 0 }, // below navbar
          height: { xs: 'calc(100% - 64px)', sm: '100%' },
        },
      }}
    >
      {(activeGroupFromStore?.name || opportunity?.chatGroup?.name) ? (
        // Only render EventDiscussionPanel when socket is connected
        isConnected ? (
          <EventDiscussionPanel
            onClose={toggleChatDrawer}
            group={activeGroupFromStore || opportunity?.chatGroup}
            eventTitle={opportunity.title}
            messages={chatState.groupChatMessages || []}
          />
        ) : (
          // Show connection status while connecting
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            p={3}
          >
            <Typography variant="h6" gutterBottom>
              {t('Connecting to Event Chat...')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('Status')}: {connectionStatus}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Please wait while we establish the connection.')}
            </Typography>
          </Box>
        )
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          {isOrganiserOrInvitedAdmin(opportunity) && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="70vh" // adjust based on your layout
            >
              {/* <Typography variant="h6" gutterBottom>
                {t('No Chat Group Created Yet')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {t('Click the button below to create a chat group and start discussion')}
              </Typography>
              <Button
                onClick={handleCreateChatGroup}
                disabled={creatingGroup}
                variant="contained"
                color="primary"
              >
                {t('Create Chat Group')}
              </Button> */}

              <Box
                sx={{
                  maxWidth: 600,
                  width: '100%',
                  textAlign: 'center', // centers text inside
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {t('No Chat Group Created Yet')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {t('Add members to the chat group and assign roles')}
                </Typography>

                {/* Searchable dropdown */}
                <Autocomplete
                  multiple
                  options={OppSubs.map((app) => app.user)}
                  getOptionLabel={(option) => option.name || option.email}
                  onChange={handleSelectUsers}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Users" placeholder="Search users..." />
                  )}
                  sx={{ mt: 2 }}
                />

                {/* Show selected users with roles */}
                {participants.length > 0 && (
                  <Paper elevation={2} sx={{ mt: 3, p: 2, textAlign: 'left' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Selected Members & Roles
                    </Typography>
                    <Grid container spacing={2}>
                      {participants.map((p) => (
                        <Grid item xs={12} key={p.userId}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ p: 1, borderBottom: '1px solid #eee' }}
                          >
                            <Typography>{p.name}</Typography>
                            <TextField
                              select
                              size="small"
                              value={p.role}
                              onChange={(e) => handleRoleChange(p.userId, e.target.value)}
                              sx={{ minWidth: 140 }}
                            >
                              {roleOptions.map((role) => (
                                <MenuItem key={role.value} value={role.value}>
                                  {role.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}
                <Spacer height="1.5rem" />
                <Typography variant="body1" gutterBottom>
                  {t('Click the button below to create a chat group and start discussion')}
                </Typography>
                <Button
                  onClick={handleCreateChatGroup}
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3 }}
                  disabled={creatingGroup || !participants.length}
                >
                  Create Chat Group
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  ) : null;

  return (
    <>
      {opportunity && opportunityMemberRole ? (
        <OpportunityMainTemplate
          viewApplication={viewApplication}
          opportunity={opportunity}
          opportunityMemberRole={opportunityMemberRole}
          refetch={refetch}
          style={{ zIndex: 100 }}
        >
          {!user && (
            <Paper
              style={{ backgroundColor: 'lightyellow', padding: 10, width: '90%', margin: 'auto' }}
            >
              <Grid container justifyContent="center">
                <Typography variant="body1" color="secondary">
                  {t('You are not logged into your account. ')}
                  <Link color="primary" href="login">
                    {t('Click here to Login')}
                  </Link>
                  {t(' or ')}
                  <Link color="primary" href="register">
                    {t('Register here')}
                  </Link>
                  {t(" if you don't have an account on SciVenia.")}
                </Typography>
              </Grid>
            </Paper>
          )}
          {!viewApplication && isMobile ? (
            <NavTabsContainer>
              <Grid item xs={12}>
                <StyledAccordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-label="Expand"
                    aria-controls="opportunity-type-content"
                    id="opportunity-type-header"
                  >
                    <StyledSummaryBoxes>
                      <Typography variant="body1" color="primary">
                        {tab}
                      </Typography>
                    </StyledSummaryBoxes>
                  </AccordionSummary>
                  <AccordionDetails>
                    <NavTabs>
                      {opportunityMenu.map((item) =>
                        item.route === 'view_opportunity' ||
                        (item.route === 'opportunity_registrations' && !isExternal) ||
                        (item.route === 'opportunity_program_agenda' && !isExternal) ? (
                          <NavTabs.Item
                            key={item.route}
                            route={item.route}
                            title={item.title}
                            params={item.params}
                          />
                        ) : opportunityHaveReviews &&
                          user &&
                          item.route === 'opportunity_reviews' &&
                          !isExternal ? (
                          <NavTabs.Item
                            key={item.route}
                            route={item.route}
                            title={item.title}
                            params={item.params}
                          />
                        ) : opportunityHaveSubmissions &&
                          // user &&
                          item.route === 'opportunity_submissions' &&
                          !isExternal ? (
                          <NavTabs.Item
                            key={item.route}
                            route={item.route}
                            title={item.title}
                            params={item.params}
                          />
                        ) : opportunityHaveCommittees &&
                          item.route === 'opportunity_committee' &&
                          !isExternal ? (
                          <NavTabs.Item
                            key={item.route}
                            route={item.route}
                            title={item.title}
                            params={item.params}
                          />
                        ) : item.route === 'opportunity_practical_info' && !isExternal ? (
                          <NavTabs.Item
                            key={item.route}
                            route={item.route}
                            title={item.title}
                            params={item.params}
                          />
                        ) : item.route === 'opportunity_feedbacks' &&
                          !isExternal &&
                          opportunityMemberRole === OPPORTUNITY_TRACK_ROLE.ORGANIZER ? (
                          <NavTabs.Item
                            key={item.route}
                            route={item.route}
                            title={item.title}
                            params={item.params}
                          />
                        ) : (
                          item.route === 'opportunity_sponsor' &&
                          !isExternal && (
                            <NavTabs.Item
                              key={item.route}
                              route={item.route}
                              title={item.title}
                              params={item.params}
                            />
                          )
                        )
                      )}
                    </NavTabs>
                  </AccordionDetails>
                </StyledAccordion>
              </Grid>
            </NavTabsContainer>
          ) : (
            !viewApplication &&
            !isMobile && (
              <NavTabsContainer style={{ backgroundColor: '#f0f0f0', paddingBottom: '10px' }}>
                <NavTabs>
                  {opportunityMenu.map((item) =>
                    item.route === 'view_opportunity' ||
                    (item.route === 'opportunity_registrations' && !isExternal) ||
                    (item.route === 'opportunity_program_agenda' && !isExternal) ? (
                      <NavTabs.Item
                        key={item.route}
                        route={item.route}
                        title={item.title}
                        params={item.params}
                      />
                    ) : opportunityHaveReviews &&
                      user &&
                      item.route === 'opportunity_reviews' &&
                      !isExternal ? (
                      <NavTabs.Item
                        key={item.route}
                        route={item.route}
                        title={item.title}
                        params={item.params}
                      />
                    ) : opportunityHaveSubmissions &&
                      // user &&
                      item.route === 'opportunity_submissions' &&
                      !isExternal ? (
                      <NavTabs.Item
                        key={item.route}
                        route={item.route}
                        title={item.title}
                        params={item.params}
                      />
                    ) : opportunityHaveCommittees &&
                      item.route === 'opportunity_committee' &&
                      !isExternal ? (
                      <NavTabs.Item
                        key={item.route}
                        route={item.route}
                        title={item.title}
                        params={item.params}
                      />
                    ) : item.route === 'opportunity_practical_info' && !isExternal ? (
                      <NavTabs.Item
                        key={item.route}
                        route={item.route}
                        title={item.title}
                        params={item.params}
                      />
                    ) : item.route === 'opportunity_feedbacks' &&
                      !isExternal &&
                      opportunityMemberRole === OPPORTUNITY_TRACK_ROLE.ORGANIZER ? (
                      <NavTabs.Item
                        key={item.route}
                        route={item.route}
                        title={item.title}
                        params={item.params}
                      />
                    ) : (
                      item.route === 'opportunity_sponsor' &&
                      !isExternal && (
                        <NavTabs.Item
                          key={item.route}
                          route={item.route}
                          title={item.title}
                          params={item.params}
                        />
                      )
                    )
                  )}
                </NavTabs>
              </NavTabsContainer>
            )
          )}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
            mt="2.875rem"
            sx={{
              transition: 'margin 0.3s',
              marginRight: {
                xs: chatDrawerOpen ? '100%' : 0,
                sm: chatDrawerOpen ? '40%' : 0,
              },
            }}
          >
            {!chatDrawerOpen && user && isAdminOrMemberOfEvent && (
              <RoundedButton
                variant="contained"
                sx={{
                  backgroundColor: isConnected ? colors.primaryColor[800] : theme.palette.grey[500],
                  '&:hover': {
                    backgroundColor: isConnected
                      ? colors.primaryColor[900]
                      : theme.palette.grey[600],
                  },
                  position: 'fixed',
                  bottom: { xs: '15px', sm: '15px' },
                  right: { xs: '16px', sm: '30px' },
                  zIndex: 1000,
                }}
                onClick={toggleChatDrawer}
                startIcon={<ChatBubbleIcon />}
              >
                {!opportunity?.chatGroup?.name
                  ? t('common:No Chat Group')
                  : !isConnected
                  ? t('common:Connecting...')
                  : t('common:Event Messages')}
              </RoundedButton>
            )}
          </Box>

          {/* Chat Drawer */}
          {chatDrawer}

          <BodyContainer>{children}</BodyContainer>
        </OpportunityMainTemplate>
      ) : (
        <HeaderWithSidebar>
          {Array.from(new Array(5)).map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <GeneralFields.Skeleton key={`skeleton-${i}`} />
          ))}
        </HeaderWithSidebar>
      )}
    </>
  );
};

ViewOpportunityDashboardTemplate.propTypes = {
  children: PropTypes.node.isRequired,
  opportunity: PropTypes.object.isRequired,
  opportunityMemberRole: PropTypes.string.isRequired,
  viewApplication: PropTypes.bool,
  refetch: PropTypes.func.isRequired,
};

ViewOpportunityDashboardTemplate.defaultProps = {
  viewApplication: false,
};

export default ViewOpportunityDashboardTemplate;
